import type { PageLink, PublicCustomer, PublicShopBasicData, PublicShopCurrency, PublicShopLanguage, PublicShopMenu, PublicShopMenuItem, ShopPageContext, ShopTemplateBlock } from "../types";
import type { ShopStorefrontMessageKey, ShopStorefrontMessageValues } from "../storefrontCatalog";
import { localizedStorefrontHref, storefrontLanguageCode, storefrontSpecialRouteMatch } from "../storefrontRouting";
import { shopStorefrontMessage } from "../storefrontCatalog";
import { getShopBlockDefaultMessageKey, getShopBlockLocalizationFields } from "../localizationSchema";
import type { PublicManufacturingSpecification } from "./shopManufacturingCustomization";

export type { PublicCustomer, PublicCustomerSession } from "../types";

export type MobileTab = string;

export const CATALOG_SALE_RETURN_PARAM = "returnTo";

export const LANGUAGE_QUERY_PARAM = "language";

export const CURRENCY_QUERY_PARAM = "currency";

export const EMPTY_SHOP_LANGUAGES: PublicShopLanguage[] = [];

export const EMPTY_SHOP_CURRENCIES: PublicShopCurrency[] = [];

export const RECENTLY_VIEWED_PRODUCTS_STORAGE_KEY = "laguna:recently-viewed-products";

export const RECENTLY_VIEWED_PRODUCT_LIMIT = 12;

export type ShopStorefrontFormatting = {
    formatDate: (value: string | null | undefined) => string;
    formatMoney: (
        value: string | number | null | undefined,
        currencyCode?: string | null,
    ) => string;
    formatNumber: (value: number) => string;
    locale: string;
};

export type ShopStorefrontMessage = (
    key: ShopStorefrontMessageKey,
    values?: ShopStorefrontMessageValues,
) => string;

export type Product = {
    id: string;
    badge?: string;
    badgeClass?: string;
    brand: string;
    name: string;
    price: string;
    previousPrice?: string;
    image: string;
    alt: string;
    imageClass: string;
    swatches: string[];
};

export type PublicProductImage = {
    mediaUuid: string;
    url: string;
    thumbnailUrl: string | null;
    alt: string;
    title: string | null;
    width: number | null;
    height: number | null;
};

export type PublicHomeProduct = {
    uuid: string;
    name: string;
    slug: string | null;
    reference: string | null;
    price: string;
    manufacturerUuid: string | null;
    manufacturerName: string | null;
    isOnSale: boolean;
    descriptionShort: string | null;
    availableForOrder: boolean;
    trackInventory: boolean;
    stockQuantity: number;
    reservedQuantity: number;
    availableQuantity: number | null;
    requiresAttribute: boolean;
    canAddToCart: boolean;
    reviewRating: number;
    reviewCount: number;
    primaryImage: PublicProductImage | null;
    images: PublicProductImage[];
    variants: PublicProductVariant[];
};

export type ShopResolvePayload = {
    pageContext?: ShopPageContext | null;
};

export type PublicProductVariant = {
    uuid: string;
    name: string;
    reference: string | null;
    price: string;
    trackInventory: boolean;
    stockQuantity: number;
    reservedQuantity: number;
    availableQuantity: number | null;
    minimalQuantity: number;
    canAddToCart: boolean;
    primaryImage: PublicProductImage | null;
    images: PublicProductImage[];
    surchargeParameters: PublicProductSurchargeParameter[];
    items: PublicProductVariantItem[];
};

export type PublicProductSurchargeParameter = {
    uuid: string;
    name: string;
    displayName: string | null;
    description: string | null;
    required: boolean;
    position: number;
    values: PublicProductSurchargeValue[];
};

export type PublicProductSurchargeValue = {
    uuid: string;
    name: string;
    salePrice: string;
    salePriceAmount: number;
    linkedProductUuid: string | null;
    linkedProductAttributeUuid: string | null;
    linkedQuantity: number;
    priceSource: "manual" | "linked_product" | string;
    availableForOrder: boolean;
    trackInventory: boolean;
    availableQuantity: number | null;
    canSelect: boolean;
    stockStatus: "not_tracked" | "in_stock" | "out_of_stock" | "unavailable" | string;
    position: number;
};

export type PublicProductVariantItem = {
    uuid: string;
    attributeUuid: string;
    attributeName: string;
    attributePosition: number;
    name: string;
    color: string | null;
    position: number;
};

export type PublicCartItem = {
    uuid: string;
    productUuid: string;
    productAttributeUuid: string | null;
    name: string;
    variantName: string | null;
    slug: string | null;
    reference: string | null;
    surcharges: PublicCartItemSurcharge[];
    quantity: number;
    unitPrice: string;
    totalPrice: string;
    availableForOrder: boolean;
    trackInventory: boolean;
    stockQuantity: number | null;
    reservedQuantity: number | null;
    availableQuantity: number | null;
    maxQuantity: number | null;
    stockStatus: "available" | "lowStock" | "outOfStock" | "unavailable" | string;
    priceChanged: boolean;
    quantityTooHigh: boolean;
    validForCheckout: boolean;
    primaryImage: PublicProductImage | null;
    manufacturingSpecification: PublicManufacturingSpecification;
};

export type PublicCartItemSurcharge = {
    parameterUuid: string;
    parameterName: string;
    valueUuid: string;
    valueName: string;
    salePrice: string;
};

export type PublicCart = {
    uuid: string;
    siteUuid: string;
    itemCount: number;
    totalQuantity: number;
    itemsTotalPrice: string;
    itemsNetPrice: string;
    itemsTaxAmount: string;
    carrierPrice: string;
    carrierNetPrice: string;
    carrierTaxRate: string;
    carrierTaxAmount: string;
    paymentSurcharge: string;
    paymentNetPrice: string;
    paymentTaxRate: string;
    paymentTaxAmount: string;
    totalPrice: string;
    totalNetPrice: string;
    totalTaxAmount: string;
    currencyCode: string;
    priceListUuid: string | null;
    priceListName: string | null;
    defaultTaxRate: string;
    carrierUuid: string | null;
    carrierRateUuid: string | null;
    paymentUuid: string | null;
    carrierPaymentOptionUuid: string | null;
    pickupPointProvider: string | null;
    pickupPointId: string | null;
    pickupPointName: string | null;
    canCheckout: boolean;
    checkoutBlockedReason: string | null;
    items: PublicCartItem[];
};

export type PublicCheckoutPaymentOption = {
    carrierPaymentOptionUuid: string;
    paymentUuid: string;
    paymentCode: string | null;
    paymentName: string;
    surcharge: string;
};

export type PublicCheckoutDeliveryOption = {
    carrierRateUuid: string;
    carrierUuid: string;
    carrierCode: string | null;
    carrierName: string;
    deliveryMethodUuid: string;
    deliveryMethodCode: string;
    deliveryMethodName: string;
    price: string;
    freeShippingThreshold: string | null;
    countryCode: string;
    currencyCode: string;
    requiresPickupPoint: boolean;
    pickupPointProvider: string | null;
    paymentOptions: PublicCheckoutPaymentOption[];
};

export type PublicCheckoutOptions = {
    cartUuid: string;
    siteUuid: string;
    itemsTotalPrice: string;
    selectedCarrierRateUuid: string | null;
    selectedCarrierPaymentOptionUuid: string | null;
    deliveryOptions: PublicCheckoutDeliveryOption[];
};

export type PublicOrderCreated = {
    uuid: string;
    reference: string;
    status: string;
    totalPrice: string;
    currencyCode: string;
    createdAt: string;
};

export type PublicOrderAddress = {
    firstname?: string | null;
    lastname?: string | null;
    company?: string | null;
    phone?: string | null;
    street?: string | null;
    city?: string | null;
    postcode?: string | null;
    countryCode?: string | null;
};

export type PublicOrderReorderResponse = {
    cart: PublicCart;
    skippedItems: Array<{
        name: string;
        productUuid: string | null;
        reason: string;
    }>;
};

export type PublicCustomerProfileUpdate = {
    firstname: string;
    lastname: string;
    email: string;
    company: string | null;
    ic: string | null;
    vat: string | null;
    newsletterSubscribed: boolean;
};

export type PublicCustomerAddressType = "home" | "work" | "other";

export type PublicCustomerAddress = {
    uuid: string;
    addressUuid: string;
    firstname: string;
    lastname: string;
    company: string | null;
    email: string | null;
    phone: string | null;
    address1: string;
    houseNumber: string | null;
    address2: string | null;
    city: string;
    postcode: string;
    countryCode: string;
    addressType: PublicCustomerAddressType;
    active: boolean;
    billingForCustomer: boolean;
    deliveryForCustomer: boolean;
    createdAt: string;
    updatedAt: string;
};

export type PublicCustomerAddressListResponse = {
    addresses: PublicCustomerAddress[];
};

export type FavoriteProductPreview = {
    id: string;
    name: string;
    href: string;
    price: string | null;
    image: string | null;
    alt: string;
    brand: string | null;
    unavailable?: boolean;
};

export type ShopStorefrontPreviewAdapter = {
    currentProductFromContext: (
        context: ShopPageContext | null | undefined,
        formatMoney: ShopStorefrontFormatting["formatMoney"],
        storefrontHref: (href: string) => string,
    ) => FavoriteProductPreview | null;
    productsForIds: (
        favoriteProductIds: Set<string>,
        previewMap: Record<string, FavoriteProductPreview>,
        message: ShopStorefrontMessage,
        formatMoney: ShopStorefrontFormatting["formatMoney"],
    ) => FavoriteProductPreview[];
    productsFromContext: (
        context: ShopPageContext | null | undefined,
        formatMoney: ShopStorefrontFormatting["formatMoney"],
        storefrontHref: (href: string) => string,
    ) => FavoriteProductPreview[];
};

export type CheckoutRouteStep = "cart" | "deliveryPayment" | "contact" | "completed";

export type CheckoutFlowHrefs = {
    cart: string;
    checkout: string;
    contact: string;
    completed: string;
};

export type CheckoutFormState = {
    email: string;
    firstname: string;
    lastname: string;
    phone: string;
    deliveryStreet: string;
    deliveryCity: string;
    deliveryPostcode: string;
    deliveryCountryCode: string;
    billingSameAsDelivery: boolean;
    billingFirstname: string;
    billingLastname: string;
    billingCompany: string;
    billingIc: string;
    billingVat: string;
    billingPhone: string;
    billingStreet: string;
    billingCity: string;
    billingPostcode: string;
    billingCountryCode: string;
    note: string;
    termsAccepted: boolean;
};

export type PickupPointSelection = {
    provider: string;
    id: string;
    name: string | null;
};

export const emptyCart: PublicCart = {
    uuid: "",
    siteUuid: "",
    itemCount: 0,
    totalQuantity: 0,
    itemsTotalPrice: "0.00",
    itemsNetPrice: "0.00",
    itemsTaxAmount: "0.00",
    carrierPrice: "0.00",
    carrierNetPrice: "0.00",
    carrierTaxRate: "0.00",
    carrierTaxAmount: "0.00",
    paymentSurcharge: "0.00",
    paymentNetPrice: "0.00",
    paymentTaxRate: "0.00",
    paymentTaxAmount: "0.00",
    totalPrice: "0.00",
    totalNetPrice: "0.00",
    totalTaxAmount: "0.00",
    currencyCode: "",
    priceListUuid: null,
    priceListName: null,
    defaultTaxRate: "0.00",
    carrierUuid: null,
    carrierRateUuid: null,
    paymentUuid: null,
    carrierPaymentOptionUuid: null,
    pickupPointProvider: null,
    pickupPointId: null,
    pickupPointName: null,
    canCheckout: false,
    checkoutBlockedReason: null,
    items: [],
};

export const emptyCheckoutOptions: PublicCheckoutOptions = {
    cartUuid: "",
    siteUuid: "",
    itemsTotalPrice: "0.00",
    selectedCarrierRateUuid: null,
    selectedCarrierPaymentOptionUuid: null,
    deliveryOptions: [],
};

export const emptyCheckoutForm: CheckoutFormState = {
    email: "",
    firstname: "",
    lastname: "",
    phone: "",
    deliveryStreet: "",
    deliveryCity: "",
    deliveryPostcode: "",
    deliveryCountryCode: "CZ",
    billingSameAsDelivery: true,
    billingFirstname: "",
    billingLastname: "",
    billingCompany: "",
    billingIc: "",
    billingVat: "",
    billingPhone: "",
    billingStreet: "",
    billingCity: "",
    billingPostcode: "",
    billingCountryCode: "CZ",
    note: "",
    termsAccepted: false,
};

export function checkoutFormValidationMessageKey(form: CheckoutFormState): ShopStorefrontMessageKey | null {
    const email = form.email.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return "ui.error.emailInvalid";
    }

    if (form.firstname.trim() === "") {
        return "ui.error.firstNameRequired";
    }

    if (form.lastname.trim() === "") {
        return "ui.error.lastNameRequired";
    }

    if (
        form.deliveryStreet.trim() === ""
        || form.deliveryCity.trim() === ""
        || form.deliveryPostcode.trim() === ""
        || form.deliveryCountryCode.trim().length !== 2
    ) {
        return "ui.error.requiredFields";
    }

    if (!form.billingSameAsDelivery) {
        if (form.billingStreet.trim() === "") {
            return "ui.error.billingStreetRequired";
        }

        if (form.billingCity.trim() === "") {
            return "ui.error.billingCityRequired";
        }

        if (form.billingPostcode.trim() === "") {
            return "ui.error.billingPostcodeRequired";
        }

        if (form.billingCountryCode.trim().length !== 2) {
            return "ui.error.billingCountryRequired";
        }
    }

    if (!form.termsAccepted) {
        return "ui.error.termsRequired";
    }

    return null;
}

export function parseStoredPublicOrderCreated(value: string | null): PublicOrderCreated | null {
    if (value === null) {
        return null;
    }

    try {
        const parsed = JSON.parse(value) as unknown;

        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }

        const order = parsed as Record<string, unknown>;
        const requiredKeys: Array<keyof PublicOrderCreated> = [
            "uuid",
            "reference",
            "status",
            "totalPrice",
            "currencyCode",
            "createdAt",
        ];

        if (!requiredKeys.every((key) => typeof order[key] === "string")) {
            return null;
        }

        return order as PublicOrderCreated;
    } catch {
        return null;
    }
}

export function customerAddressStreet(address: PublicCustomerAddress | null): string | null {
    if (address === null) {
        return null;
    }

    return [address.address1, address.houseNumber].filter(Boolean).join(" ");
}

export function checkoutFormWithCustomerDefaults(
    form: CheckoutFormState,
    customer: PublicCustomer,
    addresses: PublicCustomerAddress[] = [],
): CheckoutFormState {
    const fillEmpty = (current: string, next: string | null): string => {
        const trimmedNext = next?.trim() ?? "";

        return current.trim() === "" && trimmedNext !== "" ? trimmedNext : current;
    };
    const fillDefault = (current: string, next: string | null, defaultValue: string | null): string => {
        const trimmedCurrent = current.trim();
        const trimmedNext = next?.trim() ?? "";
        const trimmedDefault = defaultValue?.trim() ?? "";

        if (trimmedNext === "") {
            return current;
        }

        return trimmedCurrent === "" || trimmedCurrent === trimmedDefault ? trimmedNext : current;
    };
    const deliveryAddress = addresses.find((address) => address.deliveryForCustomer) ?? addresses[0] ?? null;
    const billingAddress = addresses.find((address) => address.billingForCustomer) ?? deliveryAddress;
    const billingIsDelivery = billingAddress === null
        || deliveryAddress === null
        || billingAddress.uuid === deliveryAddress.uuid
        || billingAddress.addressUuid === deliveryAddress.addressUuid;

    return {
        ...form,
        email: fillDefault(form.email, deliveryAddress?.email ?? customer.email, customer.email),
        firstname: fillDefault(form.firstname, deliveryAddress?.firstname ?? customer.firstname, customer.firstname),
        lastname: fillDefault(form.lastname, deliveryAddress?.lastname ?? customer.lastname, customer.lastname),
        phone: fillEmpty(form.phone, deliveryAddress?.phone ?? null),
        deliveryStreet: fillEmpty(form.deliveryStreet, customerAddressStreet(deliveryAddress)),
        deliveryCity: fillEmpty(form.deliveryCity, deliveryAddress?.city ?? null),
        deliveryPostcode: fillEmpty(form.deliveryPostcode, deliveryAddress?.postcode ?? null),
        deliveryCountryCode: fillEmpty(form.deliveryCountryCode === "CZ" ? "" : form.deliveryCountryCode, deliveryAddress?.countryCode ?? null) || form.deliveryCountryCode,
        billingSameAsDelivery: billingIsDelivery ? form.billingSameAsDelivery : false,
        billingFirstname: fillDefault(form.billingFirstname, billingAddress?.firstname ?? customer.firstname, customer.firstname),
        billingLastname: fillDefault(form.billingLastname, billingAddress?.lastname ?? customer.lastname, customer.lastname),
        billingCompany: fillDefault(form.billingCompany, billingAddress?.company ?? customer.company, customer.company),
        billingIc: fillEmpty(form.billingIc, customer.ic),
        billingVat: fillEmpty(form.billingVat, customer.vat),
        billingPhone: fillEmpty(form.billingPhone, billingAddress?.phone ?? null),
        billingStreet: fillEmpty(form.billingStreet, customerAddressStreet(billingAddress)),
        billingCity: fillEmpty(form.billingCity, billingAddress?.city ?? null),
        billingPostcode: fillEmpty(form.billingPostcode, billingAddress?.postcode ?? null),
        billingCountryCode: fillEmpty(form.billingCountryCode === "CZ" ? "" : form.billingCountryCode, billingAddress?.countryCode ?? null) || form.billingCountryCode,
    };
}

export const CART_ITEM_MAX_QUANTITY = 9999;

export const STOREFRONT_LOCALES = {
    cs: "cs-CZ",
    sk: "sk-SK",
    en: "en-GB",
    de: "de-DE",
    es: "es-ES",
    pl: "pl-PL",
    hu: "hu-HU",
    ro: "ro-RO",
} as const;

export const FAVORITES_STORAGE_KEY = "laguna:favorites";

export const FAVORITE_PRODUCTS_STORAGE_KEY = "laguna:favorite-products";

export function storefrontLocale(languageCode: string | null | undefined): string {
    return STOREFRONT_LOCALES[storefrontLanguageCode(languageCode)];
}

export function formatMoney(
    value: string | number | null | undefined,
    languageCode: string | null | undefined = "cs",
    currencyCode: string | null | undefined = "CZK",
): string {
    const amount = typeof value === "number" ? value : Number.parseFloat(value ?? "0");
    const normalizedAmount = Number.isFinite(amount) ? amount : 0;
    const normalizedCurrencyCode = /^[A-Z]{3}$/.test(currencyCode?.trim().toUpperCase() ?? "")
        ? currencyCode!.trim().toUpperCase()
        : "CZK";

    return new Intl.NumberFormat(storefrontLocale(languageCode), {
        currency: normalizedCurrencyCode,
        currencyDisplay: "symbol",
        maximumFractionDigits: 2,
        minimumFractionDigits: normalizedAmount % 1 === 0 ? 0 : 2,
        style: "currency",
    }).format(normalizedAmount);
}

export function formatDate(value: string | null | undefined, languageCode: string | null | undefined = "cs"): string {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(storefrontLocale(languageCode), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
}

export function moneyAmount(value: string | number | null | undefined): number {
    const amount = typeof value === "number" ? value : Number.parseFloat(value ?? "0");

    return Number.isFinite(amount) ? amount : 0;
}

export function publicCartErrorText(
    errorMessage: string,
    fallback: string,
    message: ShopStorefrontMessage,
): string {
    const trimmed = errorMessage.trim();

    if (trimmed === "") {
        return fallback;
    }

    if (trimmed === "Not enough stock.") {
        return message("ui.error.notEnoughStock");
    }

    if (trimmed === "Invalid cart item quantity.") {
        return message("ui.error.quantityRange", { max: CART_ITEM_MAX_QUANTITY, min: 1 });
    }

    if (trimmed.includes(`between 1 and ${CART_ITEM_MAX_QUANTITY}`)) {
        return message("ui.error.quantityRange", { max: CART_ITEM_MAX_QUANTITY, min: 1 });
    }

    if (trimmed.includes(`between 0 and ${CART_ITEM_MAX_QUANTITY}`)) {
        return message("ui.error.quantityRange", { max: CART_ITEM_MAX_QUANTITY, min: 0 });
    }

    if (trimmed === "This value should not be blank.") {
        return message("ui.error.quantityRequired");
    }

    if (trimmed === "Quantity is lower than product attribute minimal quantity.") {
        return message("ui.error.minimumQuantity");
    }

    if (trimmed.startsWith("Cart item quantity cannot exceed")) {
        return message("ui.error.maximumQuantity", { max: CART_ITEM_MAX_QUANTITY });
    }

    if (trimmed === "Pickup point is required for selected delivery option.") {
        return message("ui.error.pickupRequired");
    }

    if (trimmed === "Selected delivery or payment option is not active.") {
        return message("ui.error.optionInactive");
    }

    if (trimmed === "Carrier rate is not available for this cart site.") {
        return message("ui.error.deliveryUnavailable");
    }

    if (trimmed === "Payment option does not belong to selected carrier rate.") {
        return message("ui.error.paymentMismatch");
    }

    if (trimmed === "Cart cannot be checked out.") {
        return message("ui.error.cartCannotCheckout");
    }

    if (trimmed === "Delivery and payment must be selected before checkout.") {
        return message("ui.error.selectDeliveryPayment");
    }

    if (trimmed === "Delivery country not found.") {
        return message("ui.error.deliveryCountryUnsupported");
    }

    if (trimmed === "Billing country not found.") {
        return message("ui.error.billingCountryUnsupported");
    }

    if (trimmed === "Billing street is required.") {
        return message("ui.error.billingStreetRequired");
    }

    if (trimmed === "Billing city is required.") {
        return message("ui.error.billingCityRequired");
    }

    if (trimmed === "Billing postcode is required.") {
        return message("ui.error.billingPostcodeRequired");
    }

    if (trimmed === "Billing country is required.") {
        return message("ui.error.billingCountryRequired");
    }

    if (trimmed === "This value should be true.") {
        return message("ui.error.termsRequired");
    }

    return fallback;
}

export function publicCustomerErrorText(
    errorMessage: string,
    fallback: string,
    message: ShopStorefrontMessage,
): string {
    const trimmed = errorMessage.trim();

    if (trimmed === "") {
        return fallback;
    }

    if (trimmed === "Passwords do not match.") {
        return message("ui.error.passwordMismatch");
    }

    if (trimmed === "Customer account already exists.") {
        return message("ui.error.accountExists");
    }

    if (trimmed === "Invalid current password.") {
        return message("ui.error.currentPasswordInvalid");
    }

    if (trimmed === "New password must be different.") {
        return message("ui.error.newPasswordDifferent");
    }

    if (trimmed === "Invalid customer firstname.") {
        return message("ui.error.firstNameRequired");
    }

    if (trimmed === "Invalid customer lastname.") {
        return message("ui.error.lastNameRequired");
    }

    if (trimmed === "Invalid email address.") {
        return message("ui.error.emailInvalid");
    }

    if (trimmed === "Invalid email or password.") {
        return message("ui.error.credentialsInvalid");
    }

    if (trimmed === "This value should not be blank.") {
        return message("ui.error.requiredFields");
    }

    if (trimmed.includes("at least 8 characters")) {
        return message("ui.error.passwordTooShort");
    }

    return fallback;
}

export function publicOrderApiBasePath(cartApiBasePath?: string): string | null {
    if (!cartApiBasePath) {
        return null;
    }

    const normalized = cartApiBasePath.replace(/\/+$/, "");

    if (normalized.endsWith("/cart")) {
        return `${normalized.slice(0, -"/cart".length)}/orders`;
    }

    return `${normalized}/orders`;
}

export async function errorTextFromResponse(
    response: Response,
    fallback: string,
    message: ShopStorefrontMessage,
): Promise<string> {
    try {
        const contentType = response.headers.get("content-type") ?? "";

        if (contentType.includes("application/json")) {
            const data = await response.json() as { message?: unknown; errors?: Array<{ message?: unknown }> };

            if (typeof data.message === "string" && data.message.trim() !== "") {
                return publicCartErrorText(data.message, fallback, message);
            }

            const firstError = data.errors?.find((item) => typeof item.message === "string" && item.message.trim() !== "");

            if (firstError && typeof firstError.message === "string") {
                return publicCartErrorText(firstError.message, fallback, message);
            }
        }

        const text = await response.text();

        if (text.trim() !== "") {
            return publicCartErrorText(text, fallback, message);
        }
    } catch {
        return fallback;
    }

    return fallback;
}

export async function customerErrorTextFromResponse(
    response: Response,
    fallback: string,
    message: ShopStorefrontMessage,
): Promise<string> {
    try {
        const contentType = response.headers.get("content-type") ?? "";

        if (contentType.includes("application/json")) {
            const data = await response.json() as { message?: unknown; errors?: Array<{ message?: unknown }> };

            if (typeof data.message === "string" && data.message.trim() !== "") {
                return publicCustomerErrorText(data.message, fallback, message);
            }

            const firstError = data.errors?.find((item) => typeof item.message === "string" && item.message.trim() !== "");

            if (firstError && typeof firstError.message === "string") {
                return publicCustomerErrorText(firstError.message, fallback, message);
            }
        }
    } catch {
        return fallback;
    }

    return fallback;
}

export function pageHref(
    pages: PageLink[],
    pageType: string,
    fallback: string,
    languageCode: string | null | undefined,
): string {
    const page = pages.find((item) => item.pageType === pageType);

    if (page?.href && page.href.trim() !== "") {
        return localizedStorefrontHref(page.href, languageCode);
    }

    if (!page || page.slug === "") {
        return localizedStorefrontHref(fallback, languageCode);
    }

    return localizedStorefrontHref(page.slug.startsWith("/") ? page.slug : `/${page.slug}`, languageCode);
}

const AUTHORED_SYSTEM_PAGE_TYPE_BY_PATH: Readonly<Record<string, string>> = {
    "/": "home",
    "/kategorie": "category",
    "/sekce": "section",
    "/produkt": "product",
    "/kosik": "cart",
    "/objednavka": "checkout",
    "/ucet": "customerAccount",
    "/kontakt": "contact",
    "/vyhledavani": "search",
};

export function authoredSystemPageHref(
    pages: PageLink[],
    value: string | null | undefined,
    languageCode: string | null | undefined,
): string | undefined {
    const href = functionalStorefrontHref(value);

    if (!href) {
        return undefined;
    }

    let url: URL;

    try {
        url = new URL(href, "https://shop.local");
    } catch {
        return href;
    }

    if (url.origin !== "https://shop.local") {
        return href;
    }

    const authoredPath = normalizedPath(url.pathname);
    const pageType = AUTHORED_SYSTEM_PAGE_TYPE_BY_PATH[authoredPath];

    if (!pageType) {
        return localizedStorefrontHref(href, languageCode);
    }

    const resolvedPageHref = pageHref(pages, pageType, authoredPath, languageCode);
    const resolvedUrl = new URL(resolvedPageHref, "https://shop.local");

    resolvedUrl.search = url.search;
    resolvedUrl.hash = url.hash;

    return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

export function menuByLocation(basicData: PublicShopBasicData | null | undefined, location: string): PublicShopMenu | null {
    const normalizedLocation = location.trim().toLowerCase();

    return basicData?.menus.find((menu) => menu.location.trim().toLowerCase() === normalizedLocation) ?? null;
}

export function localizedMenuItems(
    items: PublicShopMenuItem[],
    languageCode: string | null | undefined,
): PublicShopMenuItem[] {
    return items.map((item) => ({
        ...item,
        href: item.href === null ? null : localizedStorefrontHref(item.href, languageCode),
        children: localizedMenuItems(item.children, languageCode),
    }));
}

export function fallbackMenuItems(pages: PageLink[], languageCode: string | null | undefined): PublicShopMenuItem[] {
    return pages.map((page) => ({
        uuid: `system-${page.pageUuid ?? page.slug}`,
        type: page.pageType ?? "page",
        label: page.title,
        href: localizedStorefrontHref(
            page.href ?? (page.slug === "" ? "/" : page.slug.startsWith("/") ? page.slug : `/${page.slug}`),
            languageCode,
        ),
        openInNewTab: false,
        children: [],
    }));
}

export type PublicStoreLink = {
    uuid: string;
    name: string;
    href: string;
    city: string;
    addressLine: string;
    email: string | null;
    phone: string | null;
    latitude: number | null;
    longitude: number | null;
    description: string | null;
    pickupEnabled: boolean;
    openingHours: Record<string, unknown> | null;
};

export type PublicStoreCity = {
    name: string;
    stores: PublicStoreLink[];
};

export function booleanValue(value: unknown, fallback = false): boolean {
    return typeof value === "boolean" ? value : fallback;
}

export function recordOrNull(value: unknown): Record<string, unknown> | null {
    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

export function storeFromRecord(value: unknown): PublicStoreLink | null {
    const data = recordOrNull(value);

    if (!data) {
        return null;
    }

    const uuid = textValue(data, "uuid");
    const name = textValue(data, "name");
    const href = textValue(data, "href");

    if (uuid === "" || name === "" || href === "") {
        return null;
    }

    return {
        uuid,
        name,
        href,
        city: textValue(data, "city"),
        addressLine: textValue(data, "addressLine"),
        email: stringOrNull(data.email),
        phone: stringOrNull(data.phone),
        latitude: numberOrNull(data.latitude),
        longitude: numberOrNull(data.longitude),
        description: stringOrNull(data.description),
        pickupEnabled: booleanValue(data.pickupEnabled),
        openingHours: recordOrNull(data.openingHours),
    };
}

export function storesFromContext(context: ShopPageContext | null | undefined): PublicStoreLink[] {
    const value = contextData(context).stores;

    return Array.isArray(value) ? value.flatMap((item) => {
        const store = storeFromRecord(item);

        return store ? [store] : [];
    }) : [];
}

export function storeCitiesFromContext(context: ShopPageContext | null | undefined): PublicStoreCity[] {
    const citiesValue = contextData(context).storeCities;

    if (Array.isArray(citiesValue)) {
        return citiesValue.flatMap((item) => {
            const data = recordOrNull(item);
            const cityStores = Array.isArray(data?.stores) ? data.stores.flatMap((storeValue) => {
                const store = storeFromRecord(storeValue);

                return store ? [store] : [];
            }) : [];
            const name = data ? textValue(data, "name") : "";

            return name !== "" && cityStores.length > 0 ? [{ name, stores: cityStores }] : [];
        });
    }

    const grouped = new Map<string, PublicStoreLink[]>();

    for (const store of storesFromContext(context)) {
        const city = store.city || shopStorefrontMessage(context?.languageCode, "ui.address.other");
        grouped.set(city, [...(grouped.get(city) ?? []), store]);
    }

    return Array.from(grouped.entries()).map(([name, stores]) => ({ name, stores }));
}

export function storeMenuItemsFromContext(context: ShopPageContext | null | undefined): PublicShopMenuItem[] {
    return storeCitiesFromContext(context).map((city) => ({
        uuid: `store-city-${city.name}`,
        type: "storeCity",
        label: city.name,
        href: null,
        openInNewTab: false,
        children: city.stores.map((store) => ({
            uuid: `store-${store.uuid}`,
            type: "store",
            label: store.name,
            href: store.href,
            openInNewTab: false,
            children: [],
        })),
    }));
}

export function enrichStoreMenuItems(items: PublicShopMenuItem[], context: ShopPageContext | null | undefined): PublicShopMenuItem[] {
    const storeChildren = storeMenuItemsFromContext(context);

    if (storeChildren.length === 0) {
        return items;
    }

    return items.map((item) => {
        const menuRoute = storefrontSpecialRouteMatch(normalizedMenuPath(menuItemHref(item)) ?? "");
        const isStoreList = item.type === "storeList" || (menuRoute?.route === "stores" && menuRoute.suffix === "");

        return {
            ...item,
            children: isStoreList && item.children.length === 0 ? storeChildren : enrichStoreMenuItems(item.children, context),
        };
    });
}

export function menuItemHref(item: PublicShopMenuItem): string {
    return item.href && item.href.trim() !== "" ? item.href : "";
}

export function functionalStorefrontHref(value: string | null | undefined): string | undefined {
    const href = value?.trim() ?? "";

    if (href === "" || href === "#" || /^(?:data|javascript|vbscript):/i.test(href)) {
        return undefined;
    }

    return href;
}

export type HeaderSelectorOption = {
    code: string;
    label: string;
    href: string;
    active: boolean;
};

export function normalizedPath(path: string): string {
    const [pathname] = path.split(/[?#]/, 1);
    const withoutTrailingSlash = pathname.replace(/\/+$/, "");

    return withoutTrailingSlash === "" ? "/" : withoutTrailingSlash;
}

export function normalizedSearch(search: string): string {
    const trimmed = search.trim();

    if (trimmed === "" || trimmed === "?") {
        return "";
    }

    return trimmed.startsWith("?") ? trimmed : `?${trimmed}`;
}

export function hrefForCurrency(currentPath: string, search: string, currencyCode: string): string {
    const params = new URLSearchParams(normalizedSearch(search));
    const code = currencyCode.trim().toUpperCase();

    if (code === "") {
        params.delete(CURRENCY_QUERY_PARAM);
    } else {
        params.set(CURRENCY_QUERY_PARAM, code);
    }

    const serialized = params.toString();

    return `${normalizedPath(currentPath)}${serialized === "" ? "" : `?${serialized}`}`;
}

export function pathWithCurrency(
    path: string,
    currencyCode: string | null | undefined,
    countryCode?: string | null,
): string {
    const code = currencyCode?.trim().toUpperCase() ?? "";

    const country = countryCode?.trim().toUpperCase() ?? "";

    if (code === "" && country === "") {
        return path;
    }

    const url = new URL(path, "https://shop.local");

    if (code !== "") {
        url.searchParams.set(CURRENCY_QUERY_PARAM, code);
    }

    if (/^[A-Z]{2}$/.test(country)) {
        url.searchParams.set("country", country);
    }

    return `${url.pathname}${url.search}`;
}

export function normalizedMenuPath(href: string): string | null {
    const trimmed = href.trim();

    if (trimmed === "" || trimmed === "#" || trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) {
        return null;
    }

    try {
        const url = new URL(trimmed, "https://shop.local");

        if (url.origin !== "https://shop.local") {
            return null;
        }

        return normalizedPath(url.pathname);
    } catch {
        const relativePath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

        return normalizedPath(relativePath);
    }
}

export function pageContextPath(context: ShopPageContext | null | undefined): string {
    if (context?.canonicalHref && context.canonicalHref.trim() !== "") {
        return normalizedPath(context.canonicalHref);
    }

    const slug = context?.slug?.trim() ?? "";

    return normalizedPath(localizedStorefrontHref(
        slug === "" ? "/" : `/${slug.replace(/^\/+/, "")}`,
        context?.languageCode,
    ));
}

export function isActiveMenuHref(href: string, currentPath: string): boolean {
    const menuPath = normalizedMenuPath(href);

    if (menuPath === null) {
        return false;
    }

    return currentPath === menuPath || (menuPath !== "/" && currentPath.startsWith(`${menuPath}/`));
}

export function menuItemMatchesContext(
    item: PublicShopMenuItem,
    currentPath: string,
    activeUuids: ReadonlySet<string>,
): boolean {
    return activeUuids.has(item.uuid)
        || isActiveMenuHref(menuItemHref(item), currentPath)
        || item.children.some((child) => menuItemMatchesContext(child, currentPath, activeUuids));
}

export function activeMenuSection(
    items: PublicShopMenuItem[],
    currentPath: string,
    activeUuids: ReadonlySet<string>,
    previewSectionUuid: string | null,
): PublicShopMenuItem | null {
    return items.find((item) => item.uuid === previewSectionUuid)
        ?? items.find((item) => menuItemMatchesContext(item, currentPath, activeUuids))
        ?? items[0]
        ?? null;
}

export function sectionMenuItems(section: PublicShopMenuItem | null): PublicShopMenuItem[] {
    return section?.children ?? [];
}

export function checkoutStepHref(checkoutHref: string, step: 1 | 2 | 3): string {
    return `${normalizedPath(checkoutHref)}${step}`;
}

export function checkoutCompletedHref(checkoutHref: string): string {
    return `${normalizedPath(checkoutHref)}-dokoncena`;
}

export function checkoutFlowHrefs(
    checkoutBaseHref: string,
    cartPageHref: string,
    flow: "multiStep" | "singlePage",
): CheckoutFlowHrefs {
    const checkout = normalizedPath(checkoutBaseHref);

    if (flow === "singlePage") {
        return {
            cart: normalizedPath(cartPageHref),
            checkout,
            contact: checkout,
            completed: checkoutCompletedHref(checkout),
        };
    }

    return {
        cart: checkoutStepHref(checkout, 1),
        checkout: checkoutStepHref(checkout, 2),
        contact: checkoutStepHref(checkout, 3),
        completed: checkoutCompletedHref(checkout),
    };
}

export function checkoutCompletedOrderStorageKey(checkoutDoneHref: string): string {
    return `webdo:shop:completed-order:${normalizedPath(checkoutDoneHref)}`;
}

export function checkoutRouteStepFromPath(checkoutHref: string, path: string): CheckoutRouteStep {
    const base = normalizedPath(checkoutHref);
    const current = normalizedPath(path);

    if (current === checkoutStepHref(base, 1)) {
        return "cart";
    }

    if (current === checkoutStepHref(base, 3)) {
        return "contact";
    }

    if (current === checkoutCompletedHref(base)) {
        return "completed";
    }

    return "deliveryPayment";
}

export const products: Product[] = [
    {
        id: "urban-move",
        badge: "-25 % S PLUS",
        badgeClass: "bg-[var(--site-primary)] text-white",
        brand: "Urban Move",
        name: "Damske tenisky",
        price: "1 299 Kc",
        image: "https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&w=700&q=85",
        alt: "Bezove tenisky Urban Move",
        imageClass: "bg-[#eeeae6]",
        swatches: ["#e7e5df", "#111111", "#ffffff"],
    },
    {
        id: "city-line",
        badge: "JEN ONLINE",
        badgeClass: "bg-black text-white",
        brand: "City Line",
        name: "Sandaly na podpatku",
        price: "899 Kc",
        image: "https://images.unsplash.com/photo-1603487742131-4160ec999306?auto=format&fit=crop&w=700&q=85",
        alt: "Cerne sandaly City Line",
        imageClass: "bg-[#eeeeee]",
        swatches: ["#111111", "#92400e"],
    },
    {
        id: "sprint",
        badge: "NOVINKA",
        badgeClass: "bg-[var(--site-primary)] text-white",
        brand: "Sprint",
        name: "Sportovni tenisky",
        price: "1 499 Kc",
        image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=700&q=85",
        alt: "Cervene sportovni tenisky",
        imageClass: "bg-[#eef3f7]",
        swatches: ["#dc2626", "#2563eb"],
    },
    {
        id: "venture",
        badge: "-30 %",
        badgeClass: "bg-red-600 text-white",
        brand: "Venture",
        name: "Volnocasove tenisky",
        price: "979 Kc",
        previousPrice: "1 399 Kc",
        image: "https://images.unsplash.com/photo-1560769629-975ec94e6a86?auto=format&fit=crop&w=700&q=85",
        alt: "Barevne tenisky",
        imageClass: "bg-[#eee9e5]",
        swatches: ["#93c5fd", "#fde047"],
    },
    {
        id: "elegance",
        brand: "Elegance",
        name: "Lodicky s otevrenou patou",
        price: "749 Kc",
        image: "https://images.unsplash.com/photo-1562183241-b937e95585b6?auto=format&fit=crop&w=700&q=85",
        alt: "Ruzove lodicky",
        imageClass: "bg-[#f0ecea]",
        swatches: ["#f9a8d4", "#111111"],
    },
];

export const productMessageKeys = [
    "ui.demo.product.womensSneakers",
    "ui.demo.product.heeledSandals",
    "ui.demo.product.sportSneakers",
    "ui.demo.product.casualSneakers",
    "ui.demo.product.openBackPumps",
] as const satisfies readonly ShopStorefrontMessageKey[];

export const productPriceValues = [1299, 899, 1499, 979, 749] as const;

export function localizedFallbackProducts(
    message: ShopStorefrontMessage,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
): Product[] {
    return products.map((product, index) => {
        const name = message(productMessageKeys[index] ?? "ui.demo.product.womensSneakers");

        return {
            ...product,
            alt: name,
            badge: index === 1
                ? message("ui.demo.product.onlineOnly")
                : index === 2 ? message("block.systemProductSummary.badge").toUpperCase() : product.badge,
            name,
            price: formatMoney(productPriceValues[index] ?? 0),
            previousPrice: index === 3 ? formatMoney(1399) : undefined,
        };
    });
}

export function favoriteProductIdsStorageKey(customerUuid: string): string {
    return `${FAVORITES_STORAGE_KEY}:${customerUuid}`;
}

export function parseFavoriteProductIds(value: string | null): Set<string> {
    if (value === null) {
        return new Set();
    }

    const parsed = JSON.parse(value) as unknown;

    if (!Array.isArray(parsed)) {
        return new Set();
    }

    return new Set(parsed.filter((item): item is string => typeof item === "string" && item.trim() !== ""));
}

function storefrontLocalStorage(storage: Storage | null | undefined): Storage | null {
    if (storage !== undefined) {
        return storage;
    }

    if (typeof window === "undefined") {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export function readFavoriteProductIds(customerUuid: string | null, storage?: Storage | null): Set<string> {
    if (customerUuid === null) {
        return new Set();
    }

    try {
        const localStorage = storefrontLocalStorage(storage);

        if (localStorage === null) {
            return new Set();
        }

        const storageKey = favoriteProductIdsStorageKey(customerUuid);
        const stored = localStorage.getItem(storageKey);

        if (stored !== null) {
            return parseFavoriteProductIds(stored);
        }

        const legacyFavorites = parseFavoriteProductIds(localStorage.getItem(FAVORITES_STORAGE_KEY));

        if (legacyFavorites.size > 0) {
            localStorage.setItem(storageKey, JSON.stringify(Array.from(legacyFavorites)));
            localStorage.removeItem(FAVORITES_STORAGE_KEY);
        }

        return legacyFavorites;
    } catch {
        return new Set();
    }
}

export function writeFavoriteProductIds(customerUuid: string | null, favorites: Set<string>, storage?: Storage | null): void {
    if (customerUuid === null) {
        return;
    }

    try {
        storefrontLocalStorage(storage)?.setItem(
            favoriteProductIdsStorageKey(customerUuid),
            JSON.stringify(Array.from(favorites)),
        );
    } catch {
        // Favorites still work for the current session when persistent storage is blocked.
    }
}

export function isFavoriteProductPreview(value: unknown): value is FavoriteProductPreview {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }

    const item = value as Record<string, unknown>;

    return typeof item.id === "string"
        && typeof item.name === "string"
        && typeof item.href === "string"
        && (typeof item.price === "string" || item.price === null)
        && (typeof item.image === "string" || item.image === null)
        && typeof item.alt === "string"
        && (typeof item.brand === "string" || item.brand === null);
}

export function readFavoriteProductPreviewMap(storage?: Storage | null): Record<string, FavoriteProductPreview> {
    try {
        const value = storefrontLocalStorage(storage)?.getItem(FAVORITE_PRODUCTS_STORAGE_KEY) ?? null;
        const parsed = value ? JSON.parse(value) as unknown : {};

        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }

        return Object.fromEntries(Object.entries(parsed as Record<string, unknown>).filter((entry): entry is [string, FavoriteProductPreview] => (
            isFavoriteProductPreview(entry[1])
        )));
    } catch {
        return {};
    }
}

export function writeFavoriteProductPreviewMap(previews: Record<string, FavoriteProductPreview>, storage?: Storage | null): void {
    try {
        storefrontLocalStorage(storage)?.setItem(FAVORITE_PRODUCTS_STORAGE_KEY, JSON.stringify(previews));
    } catch {
        // Favorites still work for the current session when persistent storage is blocked.
    }
}

export function readRecentlyViewedProducts(storage?: Storage | null): FavoriteProductPreview[] {
    try {
        const value = storefrontLocalStorage(storage)?.getItem(RECENTLY_VIEWED_PRODUCTS_STORAGE_KEY) ?? null;
        const parsed = value ? JSON.parse(value) as unknown : [];

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter(isFavoriteProductPreview).slice(0, RECENTLY_VIEWED_PRODUCT_LIMIT);
    } catch {
        return [];
    }
}

export function writeRecentlyViewedProducts(products: FavoriteProductPreview[], storage?: Storage | null): void {
    try {
        storefrontLocalStorage(storage)?.setItem(
            RECENTLY_VIEWED_PRODUCTS_STORAGE_KEY,
            JSON.stringify(products.slice(0, RECENTLY_VIEWED_PRODUCT_LIMIT)),
        );
    } catch {
        // Recently viewed products are an enhancement only.
    }
}

export function addRecentlyViewedProduct(product: FavoriteProductPreview, storage?: Storage | null): FavoriteProductPreview[] {
    const next = [
        product,
        ...readRecentlyViewedProducts(storage).filter((item) => item.id !== product.id),
    ].slice(0, RECENTLY_VIEWED_PRODUCT_LIMIT);

    writeRecentlyViewedProducts(next, storage);

    return next;
}

export function favoritePreviewFromStaticProduct(product: Product): FavoriteProductPreview {
    return {
        id: product.id,
        name: product.name,
        href: "",
        price: product.price,
        image: product.image,
        alt: product.alt,
        brand: product.brand,
    };
}

export function favoritePreviewFromPublicProduct(
    product: PublicHomeProduct,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
    storefrontHref: (href: string) => string,
): FavoriteProductPreview {
    const href = productHref(product);

    return {
        id: product.uuid,
        name: product.name,
        href: href ? storefrontHref(href) : "",
        price: formatMoney(product.price),
        image: product.primaryImage?.thumbnailUrl ?? product.primaryImage?.url ?? null,
        alt: product.primaryImage?.alt || product.name,
        brand: product.manufacturerName,
    };
}

export function favoritePreviewFromProductContext(
    context: ShopPageContext | null | undefined,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
    storefrontHref: (href: string) => string,
): FavoriteProductPreview | null {
    if (context?.pageType !== "product") {
        return null;
    }

    const data = nestedRecord(contextData(context), "product");
    const productUuid = stringOrNull(data.uuid);
    const name = textValue(data, "name", context.title);

    if (!productUuid || name.trim() === "") {
        return null;
    }

    const primaryImage = productImageFromValue(data.primaryImage);

    return {
        id: productUuid,
        name,
        href: context.canonicalHref ?? (context.slug ? storefrontHref(`/${context.slug}`) : ""),
        price: stringOrNull(data.price) ? formatMoney(stringOrNull(data.price)) : null,
        image: primaryImage?.thumbnailUrl ?? primaryImage?.url ?? null,
        alt: primaryImage?.alt || name,
        brand: stringOrNull(data.manufacturerName),
    };
}

export function favoriteProductPreviewsFromContext(
    context: ShopPageContext | null | undefined,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
    storefrontHref: (href: string) => string,
): FavoriteProductPreview[] {
    const previews = [
        ...homeProductsFromContext(context),
        ...catalogProductsFromContext(context),
    ].map((product) => favoritePreviewFromPublicProduct(
        product,
        formatMoney,
        storefrontHref,
    ));
    const productPreview = favoritePreviewFromProductContext(context, formatMoney, storefrontHref);

    return productPreview ? [...previews, productPreview] : previews;
}

export function favoriteProductPreviewsForIds(
    favoriteProductIds: Set<string>,
    previewMap: Record<string, FavoriteProductPreview>,
    message: ShopStorefrontMessage,
    _formatMoney: ShopStorefrontFormatting["formatMoney"],
): FavoriteProductPreview[] {
    return Array.from(favoriteProductIds).map((productId) => (
        previewMap[productId]
        ?? {
            id: productId,
            name: message("ui.favorite.unavailableProduct"),
            href: "",
            price: null,
            image: null,
            alt: message("ui.favorite.unavailableProduct"),
            brand: null,
            unavailable: true,
        }
    ));
}

export type ShopPageData = Record<string, unknown>;

export function contextData(context: ShopPageContext | null | undefined): ShopPageData {
    return context?.data ?? {};
}

export function nestedRecord(data: ShopPageData, key: string): Record<string, unknown> {
    const value = data[key];

    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function stringOrNull(value: unknown): string | null {
    return typeof value === "string" && value.trim() !== "" ? value : null;
}

export function activeMenuUuidsFromContext(context: ShopPageContext | null | undefined): Set<string> {
    const activeUuids = new Set<string>();
    const data = contextData(context);
    const category = nestedRecord(data, "category");
    const section = nestedRecord(data, "section");
    const categoryUuid = stringOrNull(category.uuid);
    const sectionUuid = stringOrNull(section.uuid);

    if (categoryUuid) {
        activeUuids.add(categoryUuid);
    }

    if (sectionUuid) {
        activeUuids.add(sectionUuid);
    }

    const ancestorUuids = category.ancestorUuids;

    if (Array.isArray(ancestorUuids)) {
        for (const ancestorUuid of ancestorUuids) {
            const normalizedAncestorUuid = stringOrNull(ancestorUuid);

            if (normalizedAncestorUuid) {
                activeUuids.add(normalizedAncestorUuid);
            }
        }
    }

    return activeUuids;
}

export function numberOrNull(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function productImageFromValue(value: unknown): PublicProductImage | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }

    const data = value as Record<string, unknown>;
    const mediaUuid = stringOrNull(data.mediaUuid);
    const url = stringOrNull(data.url);

    if (!mediaUuid || !url) {
        return null;
    }

    return {
        mediaUuid,
        url,
        thumbnailUrl: stringOrNull(data.thumbnailUrl),
        alt: stringOrNull(data.alt) ?? "",
        title: stringOrNull(data.title),
        width: numberOrNull(data.width),
        height: numberOrNull(data.height),
    };
}

export function productImagesFromData(data: Record<string, unknown>): PublicProductImage[] {
    const images = Array.isArray(data.images)
        ? data.images.flatMap((value) => {
            const image = productImageFromValue(value);

            return image ? [image] : [];
        })
        : [];
    const primaryImage = productImageFromValue(data.primaryImage);

    if (primaryImage && !images.some((image) => image.mediaUuid === primaryImage.mediaUuid)) {
        images.unshift(primaryImage);
    }

    return images;
}

export function productsFromValue(value: unknown): PublicHomeProduct[] {

    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicHomeProduct[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);
        const price = stringOrNull(data.price);

        if (!uuid || !name || !price) {
            return [];
        }

        return [{
            uuid,
            name,
            slug: stringOrNull(data.slug),
            reference: stringOrNull(data.reference),
            price,
            manufacturerUuid: stringOrNull(data.manufacturerUuid),
            manufacturerName: stringOrNull(data.manufacturerName),
            isOnSale: data.isOnSale === true,
            descriptionShort: stringOrNull(data.descriptionShort),
            availableForOrder: data.availableForOrder !== false,
            trackInventory: data.trackInventory === true,
            stockQuantity: numberOrNull(data.stockQuantity) ?? 0,
            reservedQuantity: numberOrNull(data.reservedQuantity) ?? 0,
            availableQuantity: numberOrNull(data.availableQuantity),
            requiresAttribute: data.requiresAttribute === true,
            canAddToCart: data.canAddToCart === true,
            reviewRating: numberOrNull(data.reviewRating) ?? 0,
            reviewCount: numberOrNull(data.reviewCount) ?? 0,
            primaryImage: productImageFromValue(data.primaryImage),
            images: productImagesFromData(data),
            variants: productVariantsFromData(data),
        }];
    });
}

export function homeProductsFromContext(context: ShopPageContext | null | undefined): PublicHomeProduct[] {
    return productsFromValue(contextData(context).products);
}

export function catalogProductsFromContext(context: ShopPageContext | null | undefined): PublicHomeProduct[] {
    return productsFromValue(nestedRecord(contextData(context), "catalog").products);
}

export function safeCatalogReturnHref(value: string | null): string | null {
    if (!value) {
        return null;
    }

    try {
        const url = new URL(value, "https://shop.local");

        if (url.origin !== "https://shop.local" || storefrontSpecialRouteMatch(url.pathname)?.route === "sale") {
            return null;
        }

        return `${normalizedPath(url.pathname)}${url.search}`;
    } catch {
        return null;
    }
}

export function productVariantsFromData(data: Record<string, unknown>): PublicProductVariant[] {
    const value = data.variants;

    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicProductVariant[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const variant = item as Record<string, unknown>;
        const uuid = stringOrNull(variant.uuid);
        const items = productVariantItemsFromData(variant.items);
        const name = stringOrNull(variant.name) ?? items.map((variantItem) => variantItem.name).join(", ");
        const price = stringOrNull(variant.price);

        if (!uuid || !name || !price) {
            return [];
        }

        return [{
            uuid,
            name,
            reference: stringOrNull(variant.reference),
            price,
            trackInventory: variant.trackInventory === true,
            stockQuantity: numberOrNull(variant.stockQuantity) ?? 0,
            reservedQuantity: numberOrNull(variant.reservedQuantity) ?? 0,
            availableQuantity: numberOrNull(variant.availableQuantity),
            minimalQuantity: numberOrNull(variant.minimalQuantity) ?? 1,
            canAddToCart: variant.canAddToCart === true,
            primaryImage: productImageFromValue(variant.primaryImage),
            images: productImagesFromData(variant),
            surchargeParameters: productSurchargeParametersFromData(variant.surchargeParameters),
            items,
        }];
    });
}

export function productSurchargeParametersFromData(value: unknown): PublicProductSurchargeParameter[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicProductSurchargeParameter[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);
        const values = productSurchargeValuesFromData(data.values);

        if (!uuid || !name || values.length === 0) {
            return [];
        }

        return [{
            uuid,
            name,
            displayName: stringOrNull(data.displayName),
            description: stringOrNull(data.description),
            required: data.required === true,
            position: numberOrNull(data.position) ?? 0,
            values,
        }];
    }).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, "cs"));
}

export function productSurchargeValuesFromData(value: unknown): PublicProductSurchargeValue[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicProductSurchargeValue[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);
        const salePrice = stringOrNull(data.salePrice) ?? "0.00";

        if (!uuid || !name) {
            return [];
        }

        return [{
            uuid,
            name,
            salePrice,
            salePriceAmount: numberOrNull(data.salePriceAmount) ?? moneyAmount(salePrice),
            linkedProductUuid: stringOrNull(data.linkedProductUuid),
            linkedProductAttributeUuid: stringOrNull(data.linkedProductAttributeUuid),
            linkedQuantity: Math.max(1, numberOrNull(data.linkedQuantity) ?? 1),
            priceSource: stringOrNull(data.priceSource) ?? "manual",
            availableForOrder: data.availableForOrder !== false,
            trackInventory: data.trackInventory === true,
            availableQuantity: numberOrNull(data.availableQuantity),
            canSelect: data.canSelect !== false,
            stockStatus: stringOrNull(data.stockStatus) ?? "not_tracked",
            position: numberOrNull(data.position) ?? 0,
        }];
    }).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, "cs"));
}

export function productVariantItemsFromData(value: unknown): PublicProductVariantItem[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicProductVariantItem[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const attributeUuid = stringOrNull(data.attributeUuid);
        const attributeName = stringOrNull(data.attributeName);
        const name = stringOrNull(data.name);

        if (!uuid || !attributeUuid || !attributeName || !name) {
            return [];
        }

        return [{
            uuid,
            attributeUuid,
            attributeName,
            attributePosition: numberOrNull(data.attributePosition) ?? 0,
            name,
            color: stringOrNull(data.color),
            position: numberOrNull(data.position) ?? 0,
        }];
    });
}

export function textValue(data: Record<string, unknown>, key: string, fallback = ""): string {
    const value = data[key];

    return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

export function animationRefreshSnapshot(blocks: ShopTemplateBlock[]): Array<Record<string, unknown>> {
    return blocks.flatMap((block) => {
        const options = block.options && typeof block.options === "object" && !Array.isArray(block.options)
            ? block.options
            : {};
        const animationOptions = Object.fromEntries(
            Object.entries(options).filter(([key]) => key.toLowerCase().includes("animation")),
        );
        const slots = block.slots && typeof block.slots === "object" && !Array.isArray(block.slots)
            ? Object.fromEntries(
                Object.entries(block.slots)
                    .map(([slotKey, slotBlocks]) => [
                        slotKey,
                        Array.isArray(slotBlocks) ? animationRefreshSnapshot(slotBlocks) : [],
                    ] as const)
                    .filter(([, slotSnapshots]) => slotSnapshots.length > 0),
            )
            : {};
        const children = Array.isArray(block.children) ? animationRefreshSnapshot(block.children) : [];
        const hasOwnAnimationOptions = Object.keys(animationOptions).length > 0;
        const hasNestedAnimationOptions = children.length > 0 || Object.keys(slots).length > 0;

        if (!hasOwnAnimationOptions && !hasNestedAnimationOptions) {
            return [];
        }

        return [{
            ...(children.length > 0 ? { children } : {}),
            options: animationOptions,
            ...(Object.keys(slots).length > 0 ? { slots } : {}),
            ...(hasOwnAnimationOptions ? { type: block.type } : {}),
        }];
    });
}

export function withLocalizedStorefrontDefaults(
    block: ShopTemplateBlock,
    languageCode: string | null | undefined,
): ShopTemplateBlock {
    const content = { ...(block.content ?? {}) };

    for (const field of getShopBlockLocalizationFields(block.type)) {
        if (!field.localizable || !field.fieldCode.startsWith("content.")) {
            continue;
        }

        const contentKey = field.fieldCode.slice("content.".length);
        const currentValue = content[contentKey];

        if (typeof currentValue === "string" && currentValue.trim() !== "") {
            continue;
        }

        const defaultMessageKey = getShopBlockDefaultMessageKey(block, field.fieldCode);

        if (defaultMessageKey) {
            content[contentKey] = shopStorefrontMessage(languageCode, defaultMessageKey);
        }
    }

    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([slotKey, slotBlocks]) => [
            slotKey,
            slotBlocks.map((slotBlock) => withLocalizedStorefrontDefaults(slotBlock, languageCode)),
        ]))
        : undefined;
    const children = block.children?.map((child) => withLocalizedStorefrontDefaults(child, languageCode));

    return {
        ...block,
        content,
        ...(children ? { children } : {}),
        ...(slots ? { slots } : {}),
    };
}

const productionDemoMediaPatterns = [
    /^https:\/\/images\.unsplash\.com\/photo-/i,
];

const productionDemoValues = new Set([
    "#",
    "#cookies",
    "#delivery",
    "#gift-cards",
    "#privacy",
    "#returns",
    "#sustainability",
    "#terms",
    "https://facebook.com",
    "https://instagram.com",
    "https://www.facebook.com/your-page",
    "https://www.instagram.com/your-page",
    "https://www.linkedin.com/company/your-page",
    "https://www.pinterest.com/your-page",
    "https://www.youtube.com/@your-channel",
    "https://x.com/your-page",
    "info@shoepoint.cz",
    "mailto:hello@example.com",
    "530 514 600",
]);

function withoutProductionDemoValue(value: unknown): unknown {
    if (typeof value !== "string") {
        return value;
    }

    const normalized = value.trim();

    if (
        productionDemoValues.has(normalized.toLowerCase())
        || productionDemoMediaPatterns.some((pattern) => pattern.test(normalized))
    ) {
        return "";
    }

    return value;
}

export function withoutProductionDemoContent(block: ShopTemplateBlock): ShopTemplateBlock {
    const content = block.content
        ? Object.fromEntries(Object.entries(block.content).map(([key, value]) => [
            key,
            withoutProductionDemoValue(value),
        ]))
        : undefined;
    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([slotKey, slotBlocks]) => [
            slotKey,
            slotBlocks.map(withoutProductionDemoContent),
        ]))
        : undefined;
    const children = block.children?.map(withoutProductionDemoContent);

    return {
        ...block,
        ...(content ? { content } : {}),
        ...(children ? { children } : {}),
        ...(slots ? { slots } : {}),
    };
}

export type AnimationMode = "appearance" | "scroll";

export type AnimationTrigger = "once" | "always";

export function productHref(product: PublicHomeProduct): string {
    return product.slug ? `/${product.slug}` : "";
}

export function addressLine(address: PublicOrderAddress): string {
    return [
        [address.firstname, address.lastname].filter(Boolean).join(" "),
        address.company,
        address.street,
        [address.postcode, address.city].filter(Boolean).join(" "),
        address.countryCode,
    ].filter((part) => typeof part === "string" && part.trim() !== "").join(", ");
}
